import { supabase } from '@/integrations/supabase/client';
import { getLogger } from '@/utils/logger';

const log = getLogger({ module: 'ProfileEmbeddingService' });

export interface ProfileEmbeddingOptions {
  profileId: string;
  silentMode?: boolean; // Don't show toast notifications
}

export interface ProfileEmbeddingResult {
  success: boolean;
  error?: string;
}

/**
 * Service for generating and managing profile embeddings
 * Handles calling the profile-embedding-worker edge function
 */
export class ProfileEmbeddingService {
  /**
   * Generate or update embeddings for a profile
   */
  static async generateEmbedding(options: ProfileEmbeddingOptions): Promise<ProfileEmbeddingResult> {
    const { profileId, silentMode = false } = options;
    
    try {
      if (!silentMode) {
        log.info(`Generating embeddings for profile ${profileId}`);
      }

      const { data, error } = await supabase.functions.invoke('profile-embedding-worker', {
        body: { profile_id: profileId }
      });

      if (error) {
        log.error('Profile embedding generation failed:', error);
        return {
          success: false,
          error: error.message || 'Failed to generate profile embedding'
        };
      }

      if (!silentMode) {
        log.info('Profile embedding generated successfully:', data);
      }

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      log.error('Error in profile embedding generation:', error);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Delete embeddings for a profile (when bio is cleared)
   */
  static async deleteEmbedding(profileId: string): Promise<ProfileEmbeddingResult> {
    try {
      log.info(`Deleting embeddings for profile ${profileId}`);

      const { error } = await supabase
        .from('profile_vectors')
        .delete()
        .eq('profile_id', profileId);

      if (error) {
        log.error('Profile embedding deletion failed:', error);
        return {
          success: false,
          error: error.message || 'Failed to delete profile embedding'
        };
      }

      log.info('Profile embedding deleted successfully');
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      log.error('Error in profile embedding deletion:', error);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Check if profile content has changed in fields that affect embeddings
   */
  static hasEmbeddingRelevantChanges(
    original: { display_name?: string; bio?: string; favorite_quote?: string },
    updated: { display_name?: string; bio?: string; favorite_quote?: string }
  ): boolean {
    return (
      original.display_name !== updated.display_name ||
      original.bio !== updated.bio ||
      original.favorite_quote !== updated.favorite_quote
    );
  }

  /**
   * Check if profile has content that should be embedded
   */
  static shouldHaveEmbedding(profile: { bio?: string; favorite_quote?: string; display_name?: string }): boolean {
    const hasBio = profile.bio && profile.bio.trim().length > 0;
    const hasQuote = profile.favorite_quote && profile.favorite_quote.trim().length > 0;
    const hasDisplayName = profile.display_name && profile.display_name.trim().length > 0;
    
    return hasBio || hasQuote || hasDisplayName;
  }
}